import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useToast } from './useToast';
import { getVideoAddress, parseImetaTag, type VideoData } from '@/lib/videoTypes';

/**
 * Publish a NIP-09 deletion request (kind 5) for a video event.
 * Works for both regular and addressable videos.
 */
export function useDeleteVideo() {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (video: VideoData) => {
      if (!user) throw new Error('You must be logged in');
      if (user.pubkey !== video.pubkey) throw new Error('You can only delete your own videos');

      const tags: string[][] = [
        ['e', video.id],
        ['k', String(video.originalEvent.kind)],
      ];
      const address = getVideoAddress(video);
      if (address) {
        tags.push(['a', address]);
      }

      await createEvent({
        kind: 5,
        content: 'Video deleted by author',
        tags,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries();
      toast({ title: 'Video deleted', description: 'A deletion request was published to your relays.' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete video',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}

export interface EditVideoInput {
  title: string;
  description: string;
  hashtags: string[];
  contentWarning?: string;
  /** Replace the thumbnail URL (optional). */
  thumbnailUrl?: string;
}

/**
 * Edit an addressable video (kind 34235/34236) by publishing a new version
 * with the same d tag. Regular events (kind 21/22) cannot be edited.
 */
export function useEditVideo() {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ video, input }: { video: VideoData; input: EditVideoInput }) => {
      if (!user) throw new Error('You must be logged in');
      if (user.pubkey !== video.pubkey) throw new Error('You can only edit your own videos');
      const address = getVideoAddress(video);
      if (!address) throw new Error('Only addressable videos (kind 34235/34236) can be edited');

      const original = video.originalEvent;
      const tags: string[][] = [];

      // Keep the d tag
      tags.push(['d', video.dTag as string]);

      // Rebuild imeta tags, optionally swapping the thumbnail URL
      const imetaTags = original.tags.filter(t => t[0] === 'imeta');
      if (imetaTags.length > 0) {
        for (const tag of imetaTags) {
          if (!input.thumbnailUrl) {
            tags.push(tag);
            continue;
          }
          const imeta = parseImetaTag(tag);
          const parts = ['imeta'];
          for (const [key, values] of Object.entries(imeta)) {
            if (key === 'image') continue;
            for (const value of values) parts.push(`${key} ${value}`);
          }
          parts.push(`image ${input.thumbnailUrl}`);
          tags.push(parts);
        }
      } else {
        // Legacy event: keep the original media tags, optionally swap thumb
        for (const tag of original.tags) {
          if (['url', 'm', 'dim', 'x', 'duration', 'published_at'].includes(tag[0])) {
            tags.push(tag);
          }
        }
        if (input.thumbnailUrl) {
          tags.push(['thumb', input.thumbnailUrl]);
        } else if (video.thumbnailUrl) {
          tags.push(['thumb', video.thumbnailUrl]);
        }
      }

      // Updated metadata
      tags.push(['title', input.title]);
      if (!original.tags.some(t => t[0] === 'published_at')) {
        tags.push(['published_at', String(video.createdAt)]);
      }

      if (input.contentWarning) {
        tags.push(['content-warning', input.contentWarning]);
      }
      for (const hashtag of input.hashtags) {
        tags.push(['t', hashtag.toLowerCase()]);
      }
      tags.push(['alt', `Video: ${input.title}`]);

      const now = Math.floor(Date.now() / 1000);
      await createEvent({
        kind: original.kind,
        content: input.description,
        tags,
        created_at: now,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries();
      toast({ title: 'Video updated', description: 'The new version was published to your relays.' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update video',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}
